import { DEFAULT_CURRENCY } from '../../../shared/billing/catalogue';
import { PLANS } from '../../../shared/billing/plan';
import { guessCurrency, listFor, type CurrencyGuess } from '../currency-guess';
import { softwareApplicationJsonLd } from '../json-ld';
import {
  DEFAULT_SELECTION,
  billingIn,
  listedPrices,
  planCards,
  planId,
  priceLines,
  pricingHref,
  selectionFrom,
  type Billing,
  type PriceLines,
  type Selection,
} from '../pricing';
import { $, $$, attr, text } from './dom';
import { countryByAddress } from './geo';
import { timeZone } from './widgets';

/* -------------------------------------------------------------------------
 * /pricing and the landing page's teaser. The server drew one price list
 * (pricingView); the same functions redraw it here in the currency for where
 * the visitor is, and when they switch billing. Where they are is worked out
 * here and not while drawing the page because the pages are cached for
 * everyone.
 * ---------------------------------------------------------------------- */

/**
 * Tells `place` which list the visitor pays from, up to twice: at once from
 * the browser's time zone, then from the country of their address, which is
 * what checkout goes by and so has the last word. A lookup that fails or
 * names no country (local development) leaves the time zone's answer, and
 * with neither the page keeps the list the server drew.
 */
function locate(place: (guess: CurrencyGuess) => void): void {
  const first = guessCurrency(timeZone(), navigator.languages ?? []);
  if (first) place(first);
  void countryByAddress().then((country) => {
    if (country) place(listFor(country));
  });
}

/** One of a card's small lines: gone when the plan has nothing to say. */
function line(el: HTMLElement | null, value: string | null): void {
  if (!el) return;
  text(el, value ?? '');
  el.hidden = value === null;
}

function paintPrices(card: HTMLElement, lines: PriceLines): void {
  text($(card, '[data-price]'), lines.price);
  text($(card, '[data-billed]'), lines.billed);
  line($(card, '[data-saving]'), lines.saving);
  line($(card, '[data-tax]'), lines.tax);
  line($(card, '[data-founding-price]'), lines.founding);
}

/** A click the browser should keep: a new tab, a new window, a download. */
function modified(e: MouseEvent): boolean {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
}

export function pricingPage(root: HTMLElement): void {
  const options = {
    appUrl: root.dataset.appUrl ?? '',
    founding: root.hasAttribute('data-founding'),
    trialDays: Number(root.dataset.trialDays),
  };
  const selection: Selection = selectionFrom({
    billing: new URLSearchParams(location.search).get('billing'),
  });
  // `converted` remembers that the visitor is outside the five currencies'
  // own countries, where the euro list is a guide to what checkout charges.
  let converted = false;

  const billings = $$<HTMLAnchorElement>(root, '[data-billing] a');
  const mark = (link: HTMLAnchorElement, on: boolean, href: string) => {
    if (on) attr(link, 'aria-current', 'true');
    else link.removeAttribute('aria-current');
    // Kept right, so "open in a new tab" still gets the page it names.
    attr(link, 'href', href);
  };

  const render = () => {
    for (const card of planCards(selection, options)) {
      const el = $(root, `[data-plan="${card.id}"]`);
      if (!el) continue;
      paintPrices(el, card);
      attr($(el, '[data-cta]'), 'href', card.href);
    }
    for (const link of billings) {
      const billing = billingIn(link.dataset.value);
      if (!billing) continue;
      mark(link, billing === selection.billing, pricingHref(billing));
    }
    const note = $(root, '[data-converted]');
    if (note) {
      note.hidden = !(converted && selection.currency === DEFAULT_CURRENCY);
    }
    // The page's SoftwareApplication states the prices the cards show, so
    // it moves to the visitor's currency with them.
    text(
      $(root, '[data-app-ld]'),
      softwareApplicationJsonLd(listedPrices(selection.currency)),
    );
  };

  // A choice redraws in place and moves the address to the page for it, so
  // a reload or a copied link shows the same billing. Other parameters (an
  // email's utm_*) are kept, bar an older link's ?currency=, which no longer
  // chooses anything.
  const choose = (billing: Billing) => {
    selection.billing = billing;
    render();
    const url = new URL(location.href);
    url.searchParams.set('billing', billing);
    url.searchParams.delete('currency');
    history.replaceState(history.state, '', url);
  };
  for (const link of billings) {
    link.addEventListener('click', (e) => {
      const billing = billingIn(link.dataset.value);
      if (!billing || modified(e)) return;
      e.preventDefault();
      choose(billing);
    });
  }

  render();
  locate((guess) => {
    selection.currency = guess.currency;
    converted = guess.converted;
    render();
  });
}

/**
 * The landing page's teaser: the default columns, in the currency for where
 * the visitor is. Only the amounts change; the buttons name a plan, not a
 * price.
 */
export function pricingTeaser(root: HTMLElement): void {
  locate(({ currency }) => {
    const selection = { ...DEFAULT_SELECTION, currency };
    for (const plan of PLANS) {
      const card = $(root, `[data-plan="${planId(plan)}"]`);
      if (card) paintPrices(card, priceLines(plan, selection, false));
    }
  });
}
